import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiUpload, apiFetchBlob } from "./client";
// B5 laedt eine Datei, keinen ack-Umschlag — dafuer gibt es den Haus-Helfer bereits.
import { downloadPdf } from "./warehouse";
import { triggerBlobDownload } from "../lib/download";

// ------------------------------------------------------------
// Einkaufsmodul W4.1 — Wire-Typen EXAKT wie Backend getPurchaseOrderFile/
// buildPurchaseOrdersListSql (Lehre PR #44: Shapes nie raten).
// Geldfelder sind bei fehlender article_purchase_prices-Berechtigung null.
// ------------------------------------------------------------
export interface PurchaseOrderListRow {
  id: number
  seq: number
  order_number: string
  status: "entwurf" | "bestellt" | "in_abwicklung" | "abgeschlossen" | "storniert"
  invoice_total_usd: number | null
  note: string | null
  created_at: string
  supplier_name: string | null
  paid_usd: number | null
  paid_eur: number | null
  shipment_count: number
  // W4.3: Anzahl ZUGESTELLTER Container dieser Bestellung ohne angelegten Wareneingang.
  we_pending_count: number
  next_eta: string | null
  /** Modul 40 A2: Summe der erfassten Nebenkosten in EUR. Optional, weil eine aeltere
   *  Backend-Fassung das Feld im Deploy-Fenster noch nicht sendet; ohne Preisrechte null. */
  costs_eur_total?: number | null
  positions_total_usd?: number | null
  positions_ohne_preis?: number | null
  ek_schaetzung_eur?: number | null
  positions_ohne_ek?: number | null
  paid_ware_usd?: number | null
  paid_ware_eur?: number | null
  zahlungen_ohne_eur?: number | null
  zahlungen_ohne_usd?: number | null
  paid_ware_nur_usd?: number | null
  paid_ware_nur_eur?: number | null
  /** Kurssummen: nur Warenzahlungen mit beiden positiven Währungsbeträgen. */
  kurs_ware_usd?: number | null
  kurs_ware_eur?: number | null
  // ---- Modul 38 / B4: nur bei ?with=summary gesetzt, sonst undefined ----
  // supplier_name und next_eta liefert die Liste schon laenger — neu sind nur die
  // beiden Mengenfelder.
  item_count?: number
  qty_total?: number
}

// ============================================================================
// Modul 38 Stufe A — VERTRAG B2/B4/B6 (eingefroren 2026-08-17)
// ============================================================================
// Spiegel von src/modules/purchase-orders/types.ts, Abschnitt "VERTRAG B2/B4/B6".
// Aenderungen NUR ueber den Koordinator — P9 (Frontend F2) setzt hierauf auf.
// Beispiel-Antworten: src/modules/purchase-orders/__contract__/modul38-beispiele.json

/** Eine Lieferantengruppe der Sammel-Anlage. */
export interface BatchOrderGroupInput {
  supplier_id: number
  /** YYYY-MM-DD. Bestimmt Monat/Jahr einer automatisch vergebenen Nummer. */
  order_date: string
  /** Weglassen = Server vergibt die naechste Nummer. Die Oberflaeche zeigt dann
   *  "wird automatisch vergeben" — KEIN Vorschauwert (bei mehreren Gruppen waere er
   *  entweder falsch oder eine Reservierung). Nur Kurzform erlaubt. */
  order_number?: string | null
  status?: "entwurf" | "bestellt"
  note?: string | null
  items: Array<{
    stock_item_id: number
    qty: number
    unit_price_usd?: number | null
    anforderungs_notiz?: string | null
    /** Plan 2026-10-20 (B3): Herkunft einer Set-Zeile — nur alle drei zusammen. */
    source_qty?: number
    source_unit?: string
    stueck_je_set?: number
    source_unit_price_usd?: number
    /** Plan 2026-10-21 (B3): eine Stueck-/pcs-Zeile wurde ausdruecklich als Paare/Sets bestaetigt. */
    paar_bestaetigt?: boolean
  }>
  /** Genau die Vormerkungen, die uebernommen werden sollen — nie "alle offenen". */
  note_ids?: number[]
}

export interface BatchCreateOrdersInput {
  include_dead?: boolean
  /** UUID. Pflicht. Wiederholung mit demselben Wert legt NICHTS neu an. */
  client_batch_id: string
  source: "reichweite" | "datei" | "hand"
  /** Version des Datei-Anlagevertrags (2 = Set-Zeilen tragen ihre Herkunft). */
  datei_vertrag?: number
  groups: BatchOrderGroupInput[]
}

export interface BatchCreatedOrder {
  id: number
  seq: number
  order_number: string
  status: "entwurf" | "bestellt"
  order_date: string
  supplier_id: number
  supplier_name: string | null
  item_count: number
  qty_total: number
  converted_note_ids: number[]
}

export interface BatchCreateOrdersResponse {
  uebersprungen?: Array<{ stock_item_id: number; sku: string; hinweis: string }>
  /** true = dieser client_batch_id war schon verarbeitet (HTTP 200 statt 201). */
  wiederholung: boolean
  client_batch_id: string
  orders: BatchCreatedOrder[]
}

// ---- B6: Vorschlag aus Datei (persistiert nichts) ----
export type VorschlagMatch = "exact" | "none"

export interface VorschlagKandidat {
  stock_item_id: number
  sku: string | null
  merchant_sku: string | null
  name: string | null
  score: number
  grund: string
  /** Plan 2026-10-21 (B1): Paar-Frage, falls dieser Kandidat gewaehlt wird. */
  paar_frage?: PaarFrage | null
}

/** Plan 2026-10-21 (B1): pcs-/Stueckzeile auf einen Grundartikel mit Faktor >= 2 — waren es
 *  Paare/Sets? Der Server rechnet nur vor; entscheiden muss der Einkaeufer. */
export interface PaarFrage {
  stueck_je_set: number
  /** null, wenn die Paar-Lesart gesperrt ist (`sperre`) — dann bleibt nur „Nein, Stück". */
  als_paar: { qty: number; unit_price_usd: number | null } | null
  /** Grund der Sperre; fehlt auf Backend-Staenden vor Review-Befund 3. */
  sperre?: string | null
  /** Letzter EK je Stueck aus frueheren Bestellungen; null ohne Preisrecht/Vergleich. */
  letzter_ek_usd: number | null
  /** Nur Hinweis (Hervorhebung), nie Vorbelegung. */
  empfehlung: "paar" | "stueck" | null
}

export interface VorschlagPosition {
  zeile: number
  raw_sku: string | null
  raw_name: string | null
  qty: number | null
  /** null ohne article_purchase_prices-Berechtigung. */
  unit_price_usd: number | null
  /** Nur bei eindeutigem Treffer gesetzt; sonst null + kandidaten zur Nutzerwahl. */
  stock_item_id: number | null
  match: VorschlagMatch
  kandidaten: VorschlagKandidat[]

  // --- Modul 43: Mengeneinheit und Betragsinvariante -----------------------
  // Alle Felder OPTIONAL: ein Backend ohne Modul 43 (oder eine aeltere Antwort
  // aus dem Cache) laesst sie weg, und die Oberflaeche muss trotzdem laufen.
  // Sie beschreiben, WIE aus der Zeile in der Datei die Bestellzeile wurde —
  // damit der Warnkasten die Rechnung zeigen kann statt sie nachzurechnen.
  /** Menge so, wie sie in der Datei steht. */
  source_qty?: number | null
  /** Einheitentext aus der Datei (`null` = die Datei hat keine Einheitenspalte). */
  source_unit?: string | null
  /** Stueck je Quell-Einheit; `null` wenn unbekannt. */
  unit_factor?: number | null
  /** Preis je QUELL-Einheit, vor der Umrechnung. */
  source_unit_price_usd?: number | null
  /** Zeilenbetrag aus der Datei, falls es eine Betragsspalte gibt. */
  source_amount_usd?: number | null
  /** Stimmt Menge x Preis mit dem ausgewiesenen Betrag ueberein? `null` = nicht pruefbar. */
  amount_ok?: boolean | null
  /** Es hat eine echte Umrechnung stattgefunden — der Einkaeufer muss sie bestaetigen. */
  requires_unit_confirmation?: boolean
  /** Zeile darf nicht uebernommen werden. */
  blocked?: boolean
  /** Deutscher Klartext, warum gesperrt wurde. */
  blocked_reason?: string | null
  /** Plan 2026-10-20 (B3): Set-Einheit des Lieferanten; `unit_factor` ist der Faktor des
   *  Grundartikels. Nur solche Zeilen senden ihre Herkunft an die Sammel-Anlage. */
  set_einheit?: boolean
  /** Plan 2026-10-21 (B1): fehlt auf alten Backend-Staenden. */
  paar_frage?: PaarFrage | null
}

export interface VorschlagResponse {
  kopf: {
    supplier_id: number | null
    supplier_name_raw: string | null
    /** Freitext-Referenz aus der Datei — nie automatisch als Bestellnummer. */
    supplier_reference_raw: string | null
    order_date_raw: string | null
    status: "bestellt"
  }
  positionen: VorschlagPosition[]
  spalten: { erkannt: Record<string, string>; verfuegbar: string[] }
  warnungen: string[]
  /** Modul 43, optional: erkannte Dokumentart. Packlisten kommen gar nicht bis hierher. */
  dokument_typ?: "commercial_invoice" | "proforma_invoice" | "order_confirmation" | "packing_list" | "unknown"
  /** Mindestens eine Zeile wurde umgerechnet — Uebernahme erst nach Bestaetigung. */
  requires_unit_confirmation?: boolean
  /** Schluessel des angewendeten Lieferantenprofils, `null` wenn keines griff. */
  lieferanten_profil?: string | null
}

/** Lagereinheit je Kopf (A-lite 18.10.2026): nur Anzeige, 'meter' fuer die Netze-Koepfe. */
export type Lagereinheit = "meter" | "stueck"

export interface PurchaseOrderItem {
  ausverkauf?: boolean
  ausverkauf_quelle?: 'dr' | 'hand' | null
  /** Live-Zusammensetzung; ältere Antworten können das Feld noch nicht enthalten. */
  bausatz_teile?: Array<{ merchant_sku: string | null; sku: string | null; name: string; menge: number }>
  id: number
  stock_item_id: number | null
  merchant_sku: string | null
  // Modul 38: Lagernummer aus stock_items.sku, per Join geliefert — KEINE neue Spalte
  // auf purchase_order_items (Wunsch P9 fuer die CSV-/PDF-Spalte "Lagernummer").
  // null, wenn die Position keinen verknuepften Artikel (mehr) hat.
  sku: string | null
  name_snapshot: string | null
  qty: number
  unit_price_usd: number | null
  anforderungs_notiz: string | null
  reklamation: string | null
  reklamation_loesung: string | null
  position: number
  /** Fehlt bei aelterem Server -> wie 'stueck'. */
  einheit?: Lagereinheit
}

export interface PurchaseShipmentItem {
  id: number
  purchase_order_item_id: number | null
  qty: number
  unit_price_usd: number | null
  cartons: number | null
  pieces_per_carton: number | null
  packstation_kartons: number
  volume_cbm: number | null
  weight_kg: number | null
  source_document_id: number | null
  carton_exception_reason: string | null
  carton_exception_by: string | null
  carton_exception_at: string | null
}

export interface PurchaseCost {
  id: number
  typ: "spedition" | "zoll" | "sonstig"
  label: string | null
  amount_eur: number | null
  status: "erwartet" | "erfasst" | "entfaellt" | "geschaetzt"
}

export interface PurchaseShipment {
  id: number
  status: "produktion" | "abholbereit" | "unterwegs" | "zugestellt" | "im_wareneingang" | "eingelagert" | "geparkt"
  label: string | null
  container_no: string | null
  bl_no: string | null
  booking_ref: string | null
  spediteur_ref: string | null
  lieferanten_ref: string | null
  crd: string | null
  etd: string | null
  eta_pod: string | null
  eta_door: string | null
  warehouse_receipt_id: number | null
  // W4.3: Status des verknuepften Wareneingangs (LEFT JOIN warehouse_receipts.status);
  // null solange kein Wareneingang angelegt ist.
  we_status: "offen" | "teilweise" | "vollstaendig" | "abgeschlossen" | null
  /** Plan 2026-10-19 (d): Ziel von „Fortsetzen“ (nur bei geparkt; null = unbekannt, fehlt bei aelterem Server). */
  status_vor_parken?: "produktion" | "abholbereit" | "unterwegs" | "zugestellt" | "im_wareneingang" | "eingelagert" | "geparkt" | null
  workflow?: {
    current_step: "container" | "packlist" | "packing_review" | "transport" | "delivery_receipt" | "storage" | "complete"
    blockers: Array<{
      code: "no_items" | "unlinked_item" | "packing_missing" | "packing_inconsistent";
      message: string;
      itemId?: number;
    }>
    can_confirm_delivery: boolean
    can_create_receipt: boolean
    has_confirmed_packlist: boolean
  }
  items: PurchaseShipmentItem[]
  costs: PurchaseCost[]
}

export interface PurchasePaymentAllocation {
  id: number
  purchase_shipment_id: number
  amount_usd: number | null
}

export interface PurchasePayment {
  id: number
  typ: "anzahlung" | "balance" | "neben"
  amount_usd: number | null
  amount_eur: number | null
  kurs: number | null
  /** Kanonischer effektiver Kurs: EUR je USD. Optional waehrend des gestuften Rollouts. */
  kurs_eur_pro_usd?: number | null
  paid_at: string | null
  beleg_ref: string | null
  note: string | null
  allocations: PurchasePaymentAllocation[]
}

export interface PurchaseEvent {
  id: number
  event_type: string
  payload: unknown
  source: "user" | "agent" | "datei"
  created_by: string | null
  created_at: string
  purchase_shipment_id: number | null
}

// ------------------------------------------------------------
// Arbeitsplatz (Schnitt 1, Plan 2026-10-17): vom Server berechnete Spuren je
// Container und eine Bestellspur — nur Ids, Status und Codes, nie Betraege.
// Das Feld ist OPTIONAL: ein aelteres Backend liefert es nicht, dann bleibt die
// Akte bei den Reitern.
// ------------------------------------------------------------

export type ArbeitsplatzSchrittStatus = "done" | "now" | "fix" | "wait" | "gate" | "idle"

export type ArbeitsplatzSchrittKey =
  | "packliste" | "packdaten" | "transport" | "ankunft" | "einlagern" | "bestand" | "einstandspreis"

export type BestellSchrittKey = "lieferungen" | "invoice" | "zahlungen" | "kosten"

export interface ArbeitsplatzCodeEintrag {
  code: string
  item_id?: number
}

export interface ArbeitsplatzSchritt<K extends string = ArbeitsplatzSchrittKey> {
  nr: number
  key: K
  status: ArbeitsplatzSchrittStatus
  codes: ArbeitsplatzCodeEintrag[]
  historisch: boolean
}

export interface ArbeitsplatzContainerSpur {
  typ: "container"
  key: string
  shipment_id: number
  schritte: ArbeitsplatzSchritt[]
}

export interface ArbeitsplatzBestellSpur {
  typ: "bestellung"
  key: "bestellung"
  schritte: ArbeitsplatzSchritt<BestellSchrittKey>[]
  saldo: "offen" | "bezahlt"
}

export interface OrderArbeitsplatz {
  modus: "entwurf" | "inaktiv" | "ohne_container" | "spuren"
  spuren: ArbeitsplatzContainerSpur[]
  bestellspur: ArbeitsplatzBestellSpur
  jetzt_dran: { spur_key: string; schritt: number } | null
  dr_push_enabled: boolean
  /** Plan 2026-10-19: alle Schritte aller Spuren erledigt (Schritt 11 „Fertig“); fehlt bei aelterem Backend. */
  fertig?: boolean
  /** Bestellung laeuft (bestellt/in_abwicklung) ohne Container; fehlt bei aelterem Backend (dann wie true). */
  container_anlegen?: boolean
}

// Rollen-Postfach (Plan 2026-10-19): ein Eintrag je offener Spur aller nicht archivierten
// Bestellungen. Rolle und Schritt liefert der Server — keine Betraege.
export type PostfachRolle = "einkauf" | "lagerleitung" | "admin_preise"

/** Spur-Schritte plus „Container anlegen“ (Bestellung ohne Container, Postfach-Luecke 2). */
export type PostfachSchrittKey = ArbeitsplatzSchrittKey | BestellSchrittKey | "container_anlegen"

export interface PostfachEintrag {
  order_id: number
  order_number: string
  supplier_name: string | null
  spur_key: string
  shipment_id: number | null
  /** "C<n>" der Akte; null fuer die Bestellspur. */
  container_nr: number | null
  container_no: string | null
  label: string | null
  eta_door: string | null
  schritt: {
    nr: number
    key: PostfachSchrittKey
    status: ArbeitsplatzSchrittStatus
    codes: ArbeitsplatzCodeEintrag[]
  }
  rolle: PostfachRolle
  ist_jetzt_dran: boolean
  // Postfach-Karten im Klartext (21.10.2026); fehlen bei einem aelteren Backend.
  /** Fachliches Bestelldatum (YYYY-MM-DD). */
  order_date?: string
  order_status?: "bestellt" | "in_abwicklung" | "abgeschlossen"
  container_anzahl?: number
  /** Laufzettel der Spur ([] bei Bestellspur und „Container anlegen“). */
  spur_schritte?: Array<{ key: ArbeitsplatzSchrittKey; status: ArbeitsplatzSchrittStatus }>
  bestell_schritte?: Array<{ key: BestellSchrittKey; status: ArbeitsplatzSchrittStatus }>
  /** Kostenposten auf „erwartet“ (Art und Bezeichnung, kein Betrag). */
  kosten_erwartet?: Array<{ container_nr: number; typ: "spedition" | "zoll" | "sonstig"; label: string | null }>
  /** "C<n>" der Container ohne Kostenposten, deren Misch-EK noch aussteht. */
  ohne_kostenposten?: number[]
}

export interface RollenPostfach {
  stand: string
  dr_push_enabled: boolean
  abgeschnitten: boolean
  eintraege: PostfachEintrag[]
}

// Restmengen je Bestellposition (Plan 2026-10-19): Mengen in der Lagereinheit der Position.
export type RestStatus = "offen" | "vollstaendig" | "ueberliefert" | "abgeschlossen"

export interface RestPosition {
  order_item_id: number
  bestellt: number
  verschifft: number
  offen: number
  ueber: number
  je_container: Array<{ shipment_id: number; qty: number }>
  status: RestStatus
  abschluss: { offen: number; grund: string; von: string | null; am: string; gueltig: boolean } | null
}

export interface OrderRestmengen {
  positionen: RestPosition[]
  offene_positionen: number
  unzugeordnet: { zeilen: number; qty: number; shipment_ids: number[] }
}

// Mengen-Tabelle je Artikel (Plan 2026-10-17 §6): bestellt -> Packliste -> WE erfasst -> an DR gemeldet.
export type MengenAuffaelligCode = "packliste_unter_bestellt" | "we_unter_packliste" | "dr_unter_we"

export interface MengenContainer {
  shipment_id: number
  packliste: number
  we_erfasst: number
  dr_gemeldet: number
  hat_we: boolean
  final_push: boolean
  ek_applied: boolean
  auffaellig: boolean
}

export interface MengenZeile {
  kopf: string
  stock_item_id: number | null
  order_item_ids: number[]
  merchant_sku: string | null
  sku: string | null
  name: string | null
  einheit: Lagereinheit
  bestellt: number
  packliste: number
  we_erfasst: number
  dr_gemeldet: number
  je_container: MengenContainer[]
  auffaellig: Array<{ code: MengenAuffaelligCode; shipment_ids: number[] }>
}

export interface OrderMengenTabelle {
  zeilen: MengenZeile[]
  auffaellige_zeilen: number
}

export interface PurchaseOrderFile {
  id: number
  seq: number
  order_number: string
  status: PurchaseOrderListRow["status"]
  supplier_id: number | null
  supplier_name: string | null
  note: string | null
  created_by: string | null
  created_at: string
  archived_at: string | null
  archived_by: string | null
  archive_reason: string | null
  invoice_total_usd: number | null
  payment_status: "offen" | "teilbezahlt" | "bezahlt" | null
  paid_usd: number | null
  /** EUR je USD aus Zahlungen mit USD- und EUR-Betrag (wie der Misch-EK); fehlt bei einem aelteren Backend. */
  effektiver_kurs_eur_pro_usd?: number | null
  items: PurchaseOrderItem[]
  shipments: PurchaseShipment[]
  payments: PurchasePayment[]
  events: PurchaseEvent[]
  /** Schnitt 1 Arbeitsplatz; fehlt bei einem aelteren Backend. */
  arbeitsplatz?: OrderArbeitsplatz
  /** Restmengen je Position (Plan 2026-10-19); fehlt bei einem aelteren Backend. */
  restmengen?: OrderRestmengen
  /** Mengen-Tabelle je Artikel (Plan 2026-10-17 §6); fehlt bei einem aelteren Backend. */
  mengen_tabelle?: OrderMengenTabelle
}

export interface PurchasePurgePreview {
  purchase_order_id: number
  order_number: string
  status: string
  can_purge: boolean
  blockers: string[]
  counts: Record<string, number>
  document_hash_count: number
  sharepoint_reference_count: number
  retained_purchase_notes: number
  manifest_sha256: string
  writes_performed: false
}

export type PurchasePurgeReasonCode =
  | "duplicate_order"
  | "erroneous_import"
  | "replacement_order"
  | "test_order"
  | "other_admin_cleanup"

const KEY = ["purchase"] as const;

/** Bestell-Liste. `status` (Modul 38 A8) ist optional und additiv: ohne Angabe
 *  verhaelt sich der Hook exakt wie bisher (alle Status, gleicher Cache-Schluessel).
 *  Mit Angabe filtert der SERVER — das Panel laedt dann nicht die ganze Liste. */
export function usePurchaseOrders(status?: PurchaseOrderListRow["status"][]) {
  const filter = status && status.length > 0 ? status.join(",") : null;
  return useQuery({
    queryKey: [...KEY, "orders", filter],
    queryFn: () => api.get<{ orders: PurchaseOrderListRow[] }>(
      filter === null ? "/purchase/orders/" : `/purchase/orders/?status=${encodeURIComponent(filter)}`,
    ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function usePurchaseOrder(id: number | null) {
  return useQuery({
    queryKey: [...KEY, "order", id],
    queryFn: () => api.get<{ order: PurchaseOrderFile }>(`/purchase/orders/${id}/`),
    enabled: id !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

/** Rollen-Postfach: eigene Abfrage, die Liste wartet nicht darauf. Unter ["purchase"] —
 *  die bestehenden Invalidierungen frischen es mit auf. */
export function useRollenPostfach() {
  return useQuery({
    queryKey: [...KEY, "postfach"],
    queryFn: () => api.get<RollenPostfach>("/purchase/orders/postfach/"),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

function useInvalidatePurchase() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: KEY });
}

/** Modul 38 B2 — Sammel-Anlage aus der Reichweite heraus.
 *
 *  EIN Aufruf fuer ALLE Lieferantengruppen: der Server legt sie in einer Transaktion an
 *  oder keine. Deshalb hier bewusst KEINE Schleife ueber Gruppen im Client — mehrere
 *  Aufrufe koennten teilweise durchlaufen und ein Wiederholungsversuch wuerde doppelt
 *  anlegen.
 *
 *  client_batch_id erzeugt der Aufrufer EINMAL pro Vorgang und schickt ihn bei einem
 *  Wiederholungsversuch unveraendert mit; der Server antwortet dann mit wiederholung=true
 *  und derselben Nutzlast, statt neue Bestellungen zu bauen.
 *
 *  Invalidiert zusaetzlich die Dashboard-Abfragen: die Reichweite blendet die bestellten
 *  Artikel danach aus, und die Vormerkungs-Liste verliert die uebernommenen Zeilen. */
export function useCreatePurchaseOrdersBatch() {
  const invalidate = useInvalidatePurchase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchCreateOrdersInput) =>
      api.post<BatchCreateOrdersResponse>("/purchase/orders/batch/", body),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function usePatchPurchaseOrder(id: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<{ updated: number }>(`/purchase/orders/${id}/`, body),
    onSuccess: invalidate,
  });
}

export function useSetPurchaseOrderStatus(id: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (status: string) => api.post<{ status: string }>(`/purchase/orders/${id}/status/`, { status }),
    onSuccess: invalidate,
  });
}

export function useAddPurchaseItems(orderId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (items: Array<{ stock_item_id: number; qty: number; unit_price_usd?: number | null; anforderungs_notiz?: string | null }>) =>
      api.post<{ added: number }>(`/purchase/orders/${orderId}/items/`, { items }),
    onSuccess: invalidate,
  });
}

export function usePatchPurchaseItem() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.patch<{ updated: number }>(`/purchase/items/${id}/`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePurchaseItem() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: number }>(`/purchase/items/${id}/`),
    onSuccess: invalidate,
  });
}

export function useTransferRequirementToGa() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (id: number) => api.post<{ transferred: number }>(`/purchase/items/${id}/uebertrag-ga/`, {}),
    onSuccess: invalidate,
  });
}

/** Rest einer Position bewusst ausweisen bzw. die Ausweisung aufheben (Recht Einkauf). */
export function useRestAbschluss() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, aktion, grund }: { id: number; aktion: "abschliessen" | "aufheben"; grund: string }) =>
      api.post<{ ergebnis: "ok" | "unveraendert"; offen: number }>(`/purchase/items/${id}/rest-abschluss/`, { aktion, grund }),
    onSuccess: invalidate,
  });
}

export function useCreatePurchaseShipment(orderId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: { label?: string | null }) =>
      api.post<{ shipment: { id: number } }>(`/purchase/orders/${orderId}/shipments/`, body),
    onSuccess: invalidate,
  });
}

export function usePatchPurchaseShipment() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.patch<{ updated: number }>(`/purchase/shipments/${id}/`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePurchaseShipment() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, grund }: { id: number; grund: string }) =>
      api.post<{ unassignedDocuments: number; releasedItems: number }>(`/purchase/shipments/${id}/delete/`, { grund }),
    onSettled: invalidate,
  });
}

export function useSetShipmentItemCartonException() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, grund }: { id: number; grund: string | null }) =>
      api.patch<{ updated: number }>(`/purchase/shipment-items/${id}/carton-exception/`, { grund }),
    onSettled: invalidate,
  });
}

export function useSetShipmentItemPackstation(shipmentItemId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kartons: number }) =>
      api.patch<{ updated: number }>(`/purchase/shipment-items/${shipmentItemId}/packstation/`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['warehouse'] });
    },
  });
}

/** Schnitt 4 (22.10.2026): Kartons und Stueck je Karton einer Containerposition direkt. */
export function useSetShipmentItemPackdaten(shipmentItemId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { cartons: number; pieces_per_carton: number }) =>
      api.patch<{ updated: number }>(`/purchase/shipment-items/${shipmentItemId}/packdaten/`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['warehouse'] });
    },
  });
}

export function useArchivePurchaseOrder(orderId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    // altbestand (admin_general): vor der Plattform manuell gefuehrt, Status bleibt stehen.
    mutationFn: (body: { grund: string; altbestand?: true }) =>
      api.post<{ archived: number }>(`/purchase/orders/${orderId}/archive/`, body),
    onSettled: invalidate,
  });
}

export function useRestorePurchaseOrder(orderId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (grund: string) => api.post<{ restored: number }>(`/purchase/orders/${orderId}/restore/`, { grund }),
    onSettled: invalidate,
  });
}

export function usePurchasePurgePreview(orderId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "order", orderId, "purge-preview"],
    queryFn: () => api.get<PurchasePurgePreview>(`/purchase/orders/${orderId}/purge-preview/`),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function usePurgePurchaseOrder(orderId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: {
      order_number_confirmation: string
      pin: string
      manifest_sha256: string
      idempotency_key: string
      reason_code: PurchasePurgeReasonCode
      irreversible_confirmation: true
    }) => api.post<Record<string, unknown>>(`/purchase/orders/${orderId}/purge/`, body),
    onSettled: invalidate,
  });
}

export function useSetPurchaseShipmentStatus() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.post<{ status: string }>(`/purchase/shipments/${id}/status/`, { status }),
    onSuccess: invalidate,
  });
}

/** Plan 2026-10-19 (d): „Status korrigieren…“ — rueckwaerts vor dem WE, Begruendung ab 10 Zeichen. */
export function useKorrigiereShipmentStatus() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, status, grund }: { id: number; status: string; grund: string }) =>
      api.post<{ status: string }>(`/purchase/shipments/${id}/status-korrektur/`, { status, grund }),
    onSuccess: invalidate,
  });
}

export type DeliveryConfirmationResult =
  | {
      outcome: "receipt_ready";
      status: PurchaseShipment["status"];
      receipt_id: number;
      already_linked: boolean;
      delivery_recorded: boolean;
    }
  // Legacy-Shape bleibt nur fuer alte, noch gecachte Frontend-Antworten typisiert.
  // Der aktuelle Server liefert Fachfehler atomar als HTTP 409 und speichert
  // keinen solchen Teilzustand mehr.
  | {
      outcome: "delivery_saved_receipt_open";
      status: "zugestellt";
      reason: "no_items" | "unlinked_items" | "unresolvable_artikel";
      delivery_recorded: boolean;
      count?: number;
    };

/** Optionaler Abgleich der am Container abgelesenen Nummer (Plan 2026-10-18 P3). */
export interface DeliveryConfirmationBody {
  container_abgleich?: { eingegeben: string };
}

/** Bestaetigt die physische Zustellung und legt danach idempotent den WE an. */
export function useConfirmShipmentDelivery(shipmentId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body?: DeliveryConfirmationBody) =>
      api.post<DeliveryConfirmationResult>(`/purchase/shipments/${shipmentId}/delivery/`, body ?? {}),
    onSuccess: invalidate,
  });
}

export function useAddPurchasePayment(orderId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ payment: { id: number } }>(`/purchase/orders/${orderId}/payments/`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePurchasePayment() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: number }>(`/purchase/payments/${id}/`),
    onSuccess: invalidate,
  });
}

/** Modul 40 B1 — Teil-Aenderung einer Zahlung aus einer Inline-Zelle des Boards.
 *
 *  Gesendet wird nur das EINE geaenderte Feld; `null` leert es. Der `typ` ist serverseitig
 *  gesperrt (eine Umwidmung haengt an den Zuordnungen — dafuer loeschen + neu anlegen), ein
 *  leerer Rumpf ist ein 400. Deshalb schickt die Zelle nie den ganzen Datensatz mit. */
export function usePatchPurchasePayment() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.patch<{ updated: number }>(`/purchase/payments/${id}/`, body),
    onSuccess: invalidate,
  });
}

export function useAddPaymentAllocation() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ paymentId, body }: { paymentId: number; body: { purchase_shipment_id: number; amount_usd: number } }) =>
      api.post<{ allocation: { id: number } }>(`/purchase/payments/${paymentId}/allocations/`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePaymentAllocation() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: number }>(`/purchase/allocations/${id}/`),
    onSuccess: invalidate,
  });
}

export function useAddPurchaseCost() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ shipmentId, body }: { shipmentId: number; body: Record<string, unknown> }) =>
      api.post<{ cost: { id: number } }>(`/purchase/shipments/${shipmentId}/costs/`, body),
    onSuccess: invalidate,
  });
}

/** Plan 2026-10-18 P1: fehlende Standard-Posten (Spedition, Zoll) als `erwartet` anlegen. Idempotent. */
export function useAddStandardKosten() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (shipmentId: number) =>
      api.post<{ angelegt: number[] }>(`/purchase/shipments/${shipmentId}/costs/standard/`, {}),
    onSuccess: invalidate,
  });
}

/** Plan 2026-10-18 P1: Admin-Freigabe ohne weitere Kosten (Recht admin_general, Begruendung Pflicht). */
export function useGebeKostenFrei() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ shipmentId, grund }: { shipmentId: number; grund: string }) =>
      api.post<{ freigegeben: number[]; geschaetzt: number[]; angelegt: number[] }>(`/purchase/shipments/${shipmentId}/costs/freigabe/`, { grund }),
    onSuccess: invalidate,
  });
}

export function usePatchPurchaseCost() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.patch<{ updated: number }>(`/purchase/costs/${id}/`, body),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------
// W4.2 Task 9: Dokumente-Reiter, Inbox, Banner
// ------------------------------------------------------------

export interface PurchaseDocument {
  id: number
  purchase_order_id: number | null
  purchase_shipment_id: number | null
  typ: string | null
  sharepoint_path: string | null
  file_name: string | null
  agent_status: string
  created_at: string
}

export interface PurchaseUploadResult {
  document_id: number
  /** `abgelegt` (0156): Upload mit „Nur ablegen“ — gespeichert und zugeordnet, ohne Auswertung. */
  outcome: "duplicate" | "stored_unparsed" | "stored_unextracted" | "extracted" | "abgelegt"
  /** Nur bei `stored_unextracted`: warum die KI-Auswertung ausblieb. */
  grund?: "ki_nicht_verfuegbar" | "ki_nicht_konfiguriert"
  sharepoint: "ok" | "failed" | "disabled"
}

export interface ReviewDiffRow {
  kategorie: "match" | "qty_diff" | "price_diff" | "nur_dokument" | "nur_bestellung"
  /** Fehlt auf alten Backend-Staenden; die UI muss dann die Uebernahme sperren. */
  matchKind?: "exact" | "unique_fallback" | "qty_price_fallback" | "ambiguous" | "unmatched" | null
  positionIndex?: number | null
  code: string | null
  orderItemId: number | null
  doc: {
    qty: number | null
    unit_price_usd: number | null
    cartons: number | null
    pieces_per_carton: number | null
    carton_quantity_ok?: boolean | null
    source_qty: number | null
    source_unit: string | null
    unit_factor: number | null
    source_unit_price_usd: number | null
    source_amount_usd: number | null
    amount_ok: boolean | null
    requires_unit_confirmation: boolean
    blocked: boolean
    blocked_reason: string | null
    /** Plan 2026-10-20 (B3): Set-Einheit des Lieferanten — `unit_factor` ist dann der
     *  Faktor des Ziel-Grundartikels. Fehlt auf alten Backend-Staenden. */
    set_einheit?: boolean
    /** Plan 2026-10-21 (B5): Stueckzeile auf ein Ziel mit Faktor >= 2 — gerechnete Paar-Lesart. */
    paar_frage?: ReviewPaarAlternative | null
  } | null
  soll: {
    qty: number
    unit_price_usd: number | null
    other_packed_qty: number
  } | null
}

export interface ReviewPaarAlternative {
  stueck_je_set: number
  /** null, wenn die Paar-Lesart gesperrt ist (`sperre`). */
  qty: number | null
  unit_price_usd: number | null
  pieces_per_carton: number | null
  sperre?: string | null
}

/** Plan 2026-10-21 (C2): bereits bestaetigter Set-Faktor einer Bestellposition. */
export interface ReviewSetBestaetigung {
  stueck_je_set: number
  menge: number
  quelle: "rechnung_assistent" | "beleg"
  document_id: number | null
  event_id: number
  am: string
}

/** Serverseitiger VORSCHLAG, mehrere Quellzeilen derselben Sternnummer zusammenzufassen. */
export interface ReviewAggregation {
  purchase_order_item_id: number
  position_indices: number[]
  code: string
}

export interface DocumentReviewResponse {
  document_id: number
  review_version: number
  purchase_order_id: number | null
  purchase_shipment_id: number | null
  doc_type: string
  rows: ReviewDiffRow[]
  /** Fehlt auf alten Backend-Staenden; ohne Zielmenge ist keine sichere Uebernahme moeglich. */
  targets?: Array<{
    order_item_id: number; merchant_sku: string | null; name: string | null; stueck_je_set?: number | null
    /** Plan 2026-10-21 (C2): fehlt auf alten Backend-Staenden. */
    bestaetigt?: ReviewSetBestaetigung | null
  }>
  /** Fehlt bei aelteren Server-Staenden — dann gibt es schlicht keine Zusammenfassung. */
  aggregations?: ReviewAggregation[]
}

export interface ConfirmDocumentMapping {
  position_index: number
  purchase_order_item_id: number
  quantity_decision?: "set_x2" | "set_faktor" | "already_pieces" | "manual_qty"
  manual_qty?: number
  /** Plan 2026-10-20 (B3): bei `set_faktor` der gesehene Faktor; weicht er ab, 409. */
  stueck_je_set?: number
  /** Plan 2026-10-21 (C3): Vorbelegung aus dieser Bestaetigung (event_id); nur mit `set_faktor`. */
  vorbelegt_aus?: number
}

/** Ausdrueckliche Zustimmung zu GENAU einer vom Server vorgeschlagenen Gruppe. */
export interface ConfirmDocumentAggregation {
  purchase_order_item_id: number
  position_indices: number[]
  confirmed: true
}

export function usePurchaseDocuments(orderId: number | null) {
  return useQuery({
    queryKey: [...KEY, "documents", orderId],
    queryFn: () => api.get<PurchaseDocument[]>(`/purchase/documents/?order_id=${orderId}`),
    enabled: orderId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function useUnmatchedDocuments() {
  return useQuery({
    queryKey: [...KEY, "documents", "unmatched"],
    queryFn: () => api.get<PurchaseDocument[]>("/purchase/documents/?unmatched=1"),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function useUploadDocument(orderId?: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: async ({ file, orderId: itemOrderId, shipmentId, nurAblegen }: {
      file: File
      orderId?: number
      shipmentId?: number
      /** „Nur ablegen (ohne Auswertung)“ — der Server verlangt dafuer eine order_id. */
      nurAblegen?: boolean
    }) => {
      // Textfelder VOR der Datei: der Server liest sie nach `request.file()`, und dort sind
      // nur Felder vor dem Dateiteil verlaesslich vorhanden — nachgestellte Felder haengen
      // davon ab, ob der Parser sie beim Ende des Dateistroms schon gesehen hat.
      const formData = new FormData();
      const targetOrderId = itemOrderId ?? orderId;
      if (targetOrderId) formData.append("order_id", String(targetOrderId));
      if (shipmentId) formData.append("shipment_id", String(shipmentId));
      if (nurAblegen) formData.append("nur_ablegen", "1");
      formData.append("file", file);
      return apiUpload<PurchaseUploadResult>("/purchase/documents/upload/", formData);
    },
    onSuccess: invalidate,
  });
}

export function useDocumentReview(docId: number | null) {
  return useQuery({
    queryKey: [...KEY, "documents", docId, "review"],
    queryFn: () => api.get<DocumentReviewResponse>(`/purchase/documents/${docId}/review/`),
    enabled: docId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function useConfirmDocument() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ docId, reviewVersion, uebernehmen, mappings, skippedPositionIndices, aggregationConfirmations }: {
      docId: number
      reviewVersion?: number
      uebernehmen: boolean
      mappings?: ConfirmDocumentMapping[]
      skippedPositionIndices?: number[]
      aggregationConfirmations?: ConfirmDocumentAggregation[]
    }) =>
      api.post<{
        document_id: number
        outcome: string
        uebernommen: number
        unveraendert?: number
        uebersprungen: number
      }>(
        `/purchase/documents/${docId}/confirm/`,
        {
          uebernehmen,
          ...(uebernehmen && reviewVersion !== undefined ? { review_version: reviewVersion } : {}),
          ...(mappings ? { mappings } : {}),
          ...(skippedPositionIndices && skippedPositionIndices.length > 0
            ? { skipped_position_indices: skippedPositionIndices }
            : {}),
          ...(aggregationConfirmations && aggregationConfirmations.length > 0
            ? { aggregation_confirmations: aggregationConfirmations }
            : {}),
        }
      ),
    onSuccess: invalidate,
  });
}

export function useRejectDocument() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ docId, grund }: { docId: number; grund: string }) =>
      api.post<{ document_id: number; outcome: string }>(
        `/purchase/documents/${docId}/reject/`,
        { grund }
      ),
    onSuccess: invalidate,
  });
}

export function useReopenDocument() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ docId, grund }: { docId: number; grund: string }) =>
      api.post<{ reopened: number }>(`/purchase/documents/${docId}/reopen/`, { grund }),
    onSettled: invalidate,
  });
}

export function useDeleteDocument() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ docId, grund }: { docId: number; grund: string }) =>
      api.post<{ document_id: number; outcome: string }>(
        `/purchase/documents/${docId}/delete/`,
        { grund }
      ),
    onSuccess: invalidate,
  });
}

/** Vorhandenes neues oder ungeprüftes Dokument ohne (weitere) Auswertung ablegen. */
export function useAblegenDocument() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ docId }: { docId: number }) =>
      api.post<{ document_id: number; outcome: "abgelegt" }>(`/purchase/documents/${docId}/ablegen/`, {}),
    onSuccess: invalidate,
  });
}

export function useAssignDocument() {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: ({ docId, orderId, shipmentId }: { docId: number; orderId: number; shipmentId?: number | null }) =>
      api.post<{ document_id: number; outcome: "zugeordnet" }>(
        `/purchase/documents/${docId}/assign/`,
        { order_id: orderId, ...(shipmentId ? { shipment_id: shipmentId } : {}) }
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------
// W4.4 Task 7: DR-Aktionen + PIN-Confirm + Misch-EK Hooks
// ------------------------------------------------------------

// Mehrere Bestellpositionen desselben Artikels (Kopf) im Container ergeben EINE Zeile:
// `stockItemId` ist der Schluessel, `orderItemIds` die abgedeckten Positionen
// (Plans/2026-10-18-mehrfachpositionen-je-kopf.md).
export interface StartmengeKandidat {
  stockItemId: number
  orderItemIds: number[]
  drStockId: string
  merchantSku: string | null
  name: string | null
  platformQty: number
  containerQty: number
  vorschlag: number
}

export interface PushResultItem {
  stockItemId: number
  orderItemIds: number[]
  ok: boolean
  valueBefore: number | null
  error?: string
}

export interface PushResponse {
  results: PushResultItem[]
}

export interface EinlagerungZeile {
  stockItemId: number
  orderItemIds: number[]
  drStockId: string | null
  merchantSku: string | null
  eingelagert: number
  vorabGepusht: number
  drIst: number
  vorschlag: number
}

export interface MischEkZeile {
  stockItemId: number
  orderItemIds: number[]
  drStockId: string | null
  merchantSku: string | null
  qty: number
  /** Bei mehreren Positionen: Ø-Preis, gewichtet nach Bestellmenge (round4). */
  unitPriceUsd: number | null
  warenwertUsd: number | null
  kostenanteilEur: number
  landedEkEur: number | null
  /** Genutzter Alt-Bestand (= Vorschlag, solange niemand ihn aendert). */
  altBestand: number
  altEkEur: number | null
  mischEkEur: number | null
  altEkFehlt: boolean
  /** Bei 'meter' sind Menge und EK je m. Fehlt bei aelterem Server -> wie 'stueck'. */
  einheit?: Lagereinheit
  /** Plan 2026-10-19 (b): Herkunft des Alt-Bestands; gelb/rot = altPflicht (Bediener bestaetigt). Fehlt bei aelterem Server. */
  altVorschlag?: number
  altQuelle?: "final_push" | "bestand_gebucht" | "fallback"
  altAmpel?: BestandAmpel | null
  altPflicht?: boolean
  altHerkunft?: AltHerkunft | null
}

export type BestandAmpel = "gruen" | "gelb" | "rot"
export type BestandGrund =
  | "kette_bruch" | "historie_fehlt" | "mehrere_zugaenge" | "anderer_container" | "abweichung"
  | "uebrige_plus" | "zugang_vor_fenster" | "zuordnung_unklar" | "kleine_menge"

export interface AltHerkunft {
  dr_zubuchung: number
  bestand_vor: number
  quelle_bestand: "historie" | "aktuell"
  uebrige_plus: number
  treffer_am: string | null
  treffer_history_ids: number[]
  gruende: string[]
  qty_jetzt: number
}

/** Plan 2026-10-19 (a′): eingefrorener Alt-Bestand-Vorschlag je Kopf (Event bestand_gebucht). */
export interface BestandBuchenPosition {
  stock_item_id: number
  order_item_ids: number[]
  eingelagert: number
  qty_jetzt: number
  qty_observed_at: string
  ampel: BestandAmpel
  alt_vorschlag: number
  treffer_history_ids: number[]
  dr_zubuchung: number
  bestand_vor: number
  quelle_bestand: "historie" | "aktuell"
  uebrige_plus: number
  gruende: BestandGrund[]
  treffer_am: string | null
}

export type BestandBuchenSperre =
  | "order_archived" | "kein_we" | "wrong_status" | "we_v1" | "we_nicht_abgeschlossen"
  | "we_unvollstaendig" | "we_klaerfaelle_offen" | "import_laeuft" | "nichts_eingelagert"

export interface BestandBuchenVorschau {
  sperre: BestandBuchenSperre | null
  positionen: BestandBuchenPosition[]
}

export type MischEkVorschauResponse =
  /** grund fehlt bei aelterem Server -> wie 'kosten_erwartet'. */
  | { blocked: true; grund?: "kosten_erwartet" | "kosten_fehlen"; offeneKosten: Array<{ typ: string; label: string | null }> }
  | { blocked: false; kurs: number | null; positionen: MischEkZeile[] }

export interface ConfirmMischEkResultItem {
  stockItemId: number
  orderItemIds: number[]
  mischEkEur: number
  ok: boolean
  error?: string
}

export interface ConfirmMischEkResponse {
  results: ConfirmMischEkResultItem[]
}

export function useStartmengeKandidaten(shipmentId: number | null) {
  return useQuery({
    queryKey: [...KEY, "shipment", shipmentId, "startmenge-kandidaten"],
    queryFn: () => api.get<{ kandidaten: StartmengeKandidat[] }>(`/purchase/shipments/${shipmentId}/startmenge-kandidaten/`),
    enabled: shipmentId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function usePushStartmenge(shipmentId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: { pin: string; items: Array<{ stock_item_id: number; qty: number }> }) =>
      api.post<PushResponse>(`/purchase/shipments/${shipmentId}/dr-push/startmenge/`, body),
    onSuccess: invalidate,
  });
}

export function useEinlagerung(shipmentId: number | null) {
  return useQuery({
    queryKey: [...KEY, "shipment", shipmentId, "einlagerung"],
    queryFn: () => api.get<{
      rechnung: EinlagerungZeile[];
      packstationOffen?: Array<{ receiptItemId: number; merchantSku: string; divertedCartons: number }>;
    }>(`/purchase/shipments/${shipmentId}/einlagerung/`),
    enabled: shipmentId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function usePushFinal(shipmentId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: { pin: string; items: Array<{ stock_item_id: number; qty: number }> }) =>
      api.post<PushResponse>(`/purchase/shipments/${shipmentId}/dr-push/final/`, body),
    onSuccess: invalidate,
  });
}

export function useMischEkVorschau(shipmentId: number | null) {
  return useQuery({
    queryKey: [...KEY, "shipment", shipmentId, "misch-ek"],
    queryFn: () => api.get<MischEkVorschauResponse>(`/purchase/shipments/${shipmentId}/misch-ek/`),
    enabled: shipmentId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

/** Plan 2026-10-19 (a): Vorschau „Bestand buchen“ — Sperrgrund und Alt-Bestand-Vorschlag je Kopf, ohne Write. */
export function useBestandBuchenVorschau(shipmentId: number | null) {
  return useQuery({
    queryKey: [...KEY, "shipment", shipmentId, "bestand-buchen"],
    queryFn: () => api.get<BestandBuchenVorschau>(`/purchase/shipments/${shipmentId}/bestand-buchen/`),
    enabled: shipmentId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

/** Plan 2026-10-19 (a): „Bestand buchen“ — Container wird eingelagert, nur Plattform-Daten, kein DR. */
export function useBestandBuchen(shipmentId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: { pin: string }) =>
      api.post<{ gebucht: true; positionen: BestandBuchenPosition[] }>(`/purchase/shipments/${shipmentId}/bestand-buchen/`, body),
    onSuccess: invalidate,
  });
}

export function useConfirmMischEk(shipmentId: number) {
  const invalidate = useInvalidatePurchase();
  return useMutation({
    mutationFn: (body: { pin: string; korrektur?: boolean; alt_bestaetigt?: Array<{ stock_item_id: number; alt_bestand: number }> }) =>
      api.post<ConfirmMischEkResponse>(`/purchase/shipments/${shipmentId}/misch-ek/confirm/`, body),
    onSuccess: invalidate,
  });
}



// ---- B5: Bestellung als PDF (Datei-Antwort, KEIN ack-Umschlag) ----

/**
 * Dateiname der PDF-Antwort.
 *
 * Spiegelt bewusst die Regel des Servers (`src/modules/purchase-orders/pdf.ts` →
 * `pdfDateiname`): Zeichen, die Dateisysteme nicht moegen, werden zu `-`, und eine
 * Bestellnummer, von der nichts uebrig bleibt, weicht auf die Id aus. Der Server setzt
 * denselben Namen im `Content-Disposition`; ein `<a download>` gewinnt aber gegen den
 * Header, deshalb muss der Name hier stehen — und deshalb steht die Quelle im Kommentar,
 * damit eine Aenderung dort auffindbar ist.
 */
export function bestellungPdfDateiname(orderNumber: string, id: number): string {
  const sauber = orderNumber.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  return `bestellung-${sauber === "" ? id : sauber}.pdf`;
}

/**
 * Laedt die Bestellung als PDF herunter.
 *
 * Nutzt den Haus-Helfer `downloadPdf` (Muster: Regal-Etiketten, Paletten-Zettel) — er
 * traegt das Token, und er liest bei einem Fehlschlag den ACK-Fehlertext des Servers,
 * statt nur den HTTP-Status zu melden. Das ist hier wichtig: 403 (kein Recht) und 404
 * (Bestellung weg) sollen als Satz ankommen, nicht als Zahl.
 */
export async function bestellungPdfHerunterladen(id: number, orderNumber: string): Promise<void> {
  await downloadPdf(`/purchase/orders/${id}/pdf/`, bestellungPdfDateiname(orderNumber, id), undefined, "GET");
}

export async function lieferantenPdfHerunterladen(id: number, orderNumber: string): Promise<void> {
  const safe = orderNumber.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  await downloadPdf(`/purchase/orders/${id}/supplier-pdf/`, `purchase-order-${safe || id}-EN.pdf`, undefined, 'GET');
}

/**
 * Laedt ein Bestell-Dokument als Datei herunter (Datei-Zellen des Boards, Modul 40 B1).
 *
 * Vorlage ist der Download-Knopf der AKTE (`PurchaseOrderDetail.tsx`, Dokument-Chips):
 * `apiFetchBlob` → `createObjectURL` → `<a download>` → `appendChild/click/remove` →
 * `revokeObjectURL`. Bewusst NICHT `bestellungPdfHerunterladen` — das laeuft ueber
 * `downloadPdf` aus `api/warehouse.ts` und ist ein anderer Weg. Board und Akte laden damit
 * dasselbe Dokument auf demselben Weg; ein Unterschied waere eine zweite Fehlerquelle ohne
 * Gegenwert. Der Dateiname kommt vom Aufrufer, weil der Server ihn nur im
 * `Content-Disposition` fuehrt und ein `<a download>` dagegen gewinnt.
 *
 * Der Aufrufer faengt den Fehler (die Zelle meldet ihn an ihrer Stelle) — hier wird nichts
 * geschluckt.
 */
export async function dokumentHerunterladen(docId: number, fileName: string): Promise<void> {
  const blob = await apiFetchBlob(`/purchase/documents/${docId}/download/`);
  triggerBlobDownload(blob, fileName);
}
